import Line from '../canvas/line'
import { IndicatorType } from '../constants/common'
import { getBarWidthWithMarginByParts } from '../utils/chart'
import { getHEXWithSpecificAplha } from '../utils/colors'
import { drawInVisibleArea } from '../utils/draw_in_visible_area'
import Indicator from './indicator'

interface Attrs {
  period: number | string
  baseLine: number
}

const DEFAULT_PARAMETERS = {
  Period: 50,
  BaseLine: 1.0,
}

function parsePeriod(periodStr: string) {
  const values = periodStr.split(',')
  const period = parseInt(values[0]) || DEFAULT_PARAMETERS.Period
  const baseLine = parseFloat(values[1]) || DEFAULT_PARAMETERS.BaseLine

  return [period, baseLine]
}

class RelativeVolume extends Indicator<Attrs> {
  static type = IndicatorType.Rvol
  static label = 'Relative Volume'

  rvol: number[] = []
  declare period: number
  declare baseLine: number

  set(values: Partial<Attrs>) {
    if (typeof values.period === 'string') {
      const [period = 0, baseLine = 0] = parsePeriod(values.period)
      super.set({ ...values, period, baseLine })
    } else {
      super.set(values)
    }
  }

  compute() {
    if (this.isComputeNecessary()) {
      this.rvol = []

      let sum = 0
      for (let i = 0; i < this.period; i++) {
        sum += this.data.volume[i]
      }
      for (let i = this.period; i < this.data.volume.length; i++) {
        sum = sum - this.data.volume[i - this.period] + this.data.volume[i]
        this.rvol[i] = sum > 0 ? this.data.volume[i] / (sum / this.period) : 0
      }

      this.lastValue = this.rvol.last() ?? null
    }

    const { min, max } =
      this.rvol.length > 0
        ? this.computeVisibleMinMax([...this.rvol, this.baseLine])
        : this.getDomainDefaults(this.type)
    this.min = min
    this.max = max
  }

  renderIndicator(context: CanvasRenderingContext2D) {
    const { ChartSettings } = this.getChartLayoutSettings()
    const { Colors } = ChartSettings.general
    const chartModel = this.model.chart()
    const { zoomFactor } = chartModel
    const { barFillWidth, barBorderWidth } = getBarWidthWithMarginByParts({
      zoomFactor,
      chartLayout: chartModel.chart_layout(),
    })
    const barWidthWithoutMargin = barFillWidth + barBorderWidth * 2
    const halfBarWidth = ~~Math.max(1, barWidthWithoutMargin / 2)

    drawInVisibleArea({
      quote: this.data,
      paneModel: this.model,
      leftOffset: this.leftOffset,
      width: this.width,
      fromIndexOffset: this.period + 1,
      drawBarCallback: (i, x) => {
        if (this.rvol[i] === undefined) return

        const baseLine = Math.round(this.fy(this.baseLine))
        const y = Math.round(this.fy(this.rvol[i]))

        context.fillStyle = this.rvol[i] < this.baseLine ? Colors.volumeTrendDown : Colors.volumeTrendUp
        context.fillRect(x - halfBarWidth, baseLine, barWidthWithoutMargin, y - baseLine)
      },
    })

    const baseLineY = this.fy(this.baseLine)
    new Line(
      {
        x1: -this.leftOffset,
        x2: -this.leftOffset + this.contentWidth,
        y1: Math.round(baseLineY),
        y2: Math.round(baseLineY),
        strokeStyle: getHEXWithSpecificAplha(Colors.zeroChange, 0.5),
      },
      this.model
    ).render(context)
  }

  getModalConfig() {
    const period = {
      type: 'number',
      label: 'Period',
      name: 'period',
      value: this.period ?? DEFAULT_PARAMETERS.Period,
      required: true,
      min: 1,
      max: 999999,
    }

    const baseLine = {
      type: 'number',
      label: 'Baseline',
      name: 'baseLine',
      value: this.baseLine ?? DEFAULT_PARAMETERS.BaseLine,
      required: true,
      step: 0.1,
      allowDecimal: true,
      min: 0,
      max: 999999,
    }

    return {
      title: this.label,
      inputs: [period, baseLine],
      inputsErrorMessages: {
        period: `${period.label} must be a whole number between ${period.min} and ${period.max}`,
        baseLine: `${baseLine.label} must be a number between ${baseLine.min} and ${baseLine.max}`,
      },
    }
  }

  getIsValid(key: string): boolean {
    switch (key) {
      case 'period':
        return this.getIsNumberInputValid({ key })
      case 'baseLine':
        return this.getIsNumberInputValid({ key, integerOnly: false })
      default:
        return false
    }
  }

  toString() {
    return `RVOL ${this.period}, ${this.baseLine}, SMA`
  }
}

export default RelativeVolume
